import axios from "axios";
import { readRemoteSession } from "../context/RemoteLaunchContext";
import AuthService from "./auth";

/**
 * The headers every call in this file sends.
 *
 * X-PT-Session is how the server knows which *visit* a request belongs to.
 * The JWT identifies the kiosk and therefore the venue, but a standalone web
 * player and a machine on the floor can share one kiosk-shaped identity, and
 * the on-site check is a property of the visit rather than of the venue — so
 * the ticket write has to be able to ask "did this session clear the gate".
 *
 * A header rather than a body field on purpose: four separate POST helpers
 * below reach the gated endpoints, and threading a new argument through all of
 * them and their call sites buys nothing over setting it once here. Kiosk
 * sessions have nothing to send and send nothing, which is the correct answer
 * and not a missing value.
 *
 * The server must name x-pt-session in CORS_ALLOW_HEADERS or every one of
 * these calls fails at the preflight. See pt3server/settings.py.
 */
function authHeaders(){
    var auth = AuthService.getCurrentAuth();
    var headers = {
        'Authorization': `Bearer ${auth && auth.access}`,
        'Content-Type': 'application/json'
    };
    var session = readRemoteSession();
    if(session && session.sessionId){
        headers['X-PT-Session'] = session.sessionId;
    }
    return headers;
}


const API_URL = process.env.REACT_APP_API_URL + "/api";

class GameService {
    getVenueAndGames(){
        var config = {
            method: 'get',
            url: API_URL + "/venues",
            headers: authHeaders()
        };
        return axios(config).then(response => {
            //console.log(response);
            return response.data;
        });
    }

    getPromo(promo_id){
        var config = {
            method: 'get',
            url: API_URL + `/promos/${promo_id}`,
            headers: authHeaders()
        };
        return axios(config).then(response => {
            //console.log(response);
            return response.data;
        });
    }

    getEvents(promo_id){
        var config = {
            method: 'get',
            url: API_URL + `/promos/${promo_id}/events`,
            headers: authHeaders()
        };
        return axios(config).then(response => {
            //console.log(response);
            return response.data;
        });
    }

    getPlayer(card_num){
        var config = {
            method: 'get',
            url: API_URL + `/players/${card_num}`,
            headers: authHeaders()
        };
        return axios(config).then(response => {
            //console.log(response);
            return response.data;
        });
    }

    getPlayerCardNumberRegistered(card_num){
        var config = {
            method: 'get',
            url: API_URL + `/players/iscardnumberregistered/${card_num}`,
            headers: authHeaders()
        };
        return axios(config).then(response => {
            //console.log(response);
            return response.data;
        });
    }


    postSearchPlayer(email, firstname, lastname){
        var config = {
            method: 'post',
            url: API_URL + `/players/search/`,
            headers: authHeaders(),
            data : JSON.stringify({
                "email": email,
                "firstname": firstname,
                "lastname": lastname
            })
        };
        return axios(config).then(response => {
            //console.log(response);
            return response.data;
        });
    }

    postNewPlayer(card_num, email, firstname, lastname, badge = ""){
                
        var post_data = null
        if(badge == ""){
            post_data = {
                "cardnumber": card_num,
                "email": email,
                "firstname": firstname,
                "lastname": lastname,
            }
        }else{
            post_data = {
                "cardnumber": card_num,
                "clubnumber": badge,
                "email": email,
                "firstname": firstname,
                "lastname": lastname,
            }
        }

        var config = {
            method: 'post',
            url: API_URL + "/players",
            headers: authHeaders(),
            data : JSON.stringify(post_data)
        };
        return axios(config).then(response => {
            //console.log(response);
            return response.data;
        });
    }

    postNewClubMember(club_num, email, firstname, lastname){
        var config = {
            method: 'post',
            url: API_URL + "/players/clubmember/",
            headers: authHeaders(),
            data : JSON.stringify({
                "clubnumber": club_num,
                "email": email,
                "firstname": firstname,
                "lastname": lastname,
            })
        };
        return axios(config).then(response => {
            return response.data;
        });
    }

    /**
     * Is this club number anybody? Resolves to the player, rejects with 404 if not.
     *
     * Same endpoint as postNewClubMember but with lookup_only, which suppresses
     * the create. The standalone web promotion needs to ask before it writes:
     * a venue with web_allow_new_players off has to turn an unrecognised number
     * away, and it cannot do that if the act of asking already created the
     * player. See docs/standalone-web-promotion-implementation.md §4.4.
     */
    lookupClubMember(club_num){
        var config = {
            method: 'post',
            url: API_URL + "/players/clubmember/",
            headers: authHeaders(),
            data : JSON.stringify({
                "clubnumber": club_num,
                "lookup_only": true,
            })
        };
        return axios(config).then(response => {
            return response.data;
        });
    }

    postUpdatePlayer(player_id, firstname, lastname, email){
        var config = {
            method: 'post',
            url: API_URL + "/players/update/",
            headers: authHeaders(),
            data : JSON.stringify({
                "playerid": player_id,
                "firstname": firstname,
                "lastname": lastname,
                "email": email
            })
        };
        return axios(config).then(response => {
            //console.log(response);
            return response.data;
        });
    }

    postAddPlayerCard(player_id, card_num){
        var config = {
            method: 'post',
            url: API_URL + "/players/addplayercard/",
            headers: authHeaders(),
            data : JSON.stringify({
                "playerid": player_id,
                "cardnumber": card_num
            })
        };
        return axios(config).then(response => {
            //console.log(response);
            return response.data;
        });
    }

    postNewGuestPlayer(card_num, email, firstname, lastname, phonenumber){
        var config = {
            method: 'post',
            url: API_URL + "/players",
            headers: authHeaders(),
            data : JSON.stringify({
                "cardnumber": card_num,
                "email": email,
                "firstname": firstname,
                "lastname": lastname,
                "phonenumber": phonenumber
            })
        };
        return axios(config).then(response => {
            //console.log(response);
            return response.data;
        });
    }

    postTicket(playerid, promoid, promoperiodid, eventchoiceidslist, tiebreaker, playedatlocal){
        var config = {
            method: 'post',
            url: API_URL + "/tickets",
            headers: authHeaders(),
            data : JSON.stringify({
                "playerid": playerid,
                "promoid": promoid,
                "promoperiodid": promoperiodid,
                "eventchoiceidslist": eventchoiceidslist,
                "tiebreaker": tiebreaker,
                "playedatlocal": playedatlocal
            })
        };

        return axios(config).then(response => {
            //console.log(response);
            return response.data;
        });
    }

    postTicketWOTiebreaker(playerid, promoid, promoperiodid, eventchoiceidslist, playedatlocal){
        var config = {
            method: 'post',
            url: API_URL + "/tickets",
            headers: authHeaders(),
            data : JSON.stringify({
                "playerid": playerid,
                "promoid": promoid,
                "promoperiodid": promoperiodid,
                "eventchoiceidslist": eventchoiceidslist,
                "playedatlocal": playedatlocal
            })
        };

        return axios(config).then(response => {
            //console.log(response);
            return response.data;
        });
    }

    // Survivor Pool pool standings — who else is still alive.
    //
    // Anonymised server-side: club numbers only, never names. Same projection
    // the venue display screen renders, so the kiosk and the TV in the room
    // cannot disagree about who is left.
    getSurvivorStandings(promoid){
        var config = {
            method: 'get',
            url: API_URL + `/promos/${promoid}/survivor/standings`,
            headers: authHeaders()
        };

        return axios(config).then(response => {
            return response.data;
        });
    }

    /* The Survivor QA clock. See pt3server/pt3backendapi/common/clock.py.
     *
     * Reading the state needs nothing: every Survivor screen asks, so it can
     * show the banner when the clock has been moved. Moving it needs the kiosk
     * JWT, and the server refuses outright unless the override is enabled —
     * which cannot be true outside DEBUG.
     */
    getSurvivorClock(){
        var config = {
            method: 'get',
            url: API_URL + "/survivor/clock",
            headers: { 'Content-Type': 'application/json' }
        };
        return axios(config).then(response => response.data);
    }

    setSurvivorClock(apparent_iso){
        var config = {
            method: 'post',
            url: API_URL + "/survivor/clock",
            headers: authHeaders(),
            data: JSON.stringify(
                apparent_iso ? {apparent: apparent_iso} : {clear: true}
            )
        };
        return axios(config).then(response => response.data);
    }

    /* SUR-460..SUR-466 — the pool boards, in the entry manager's format.
     *
     * Venue-wide: the kiosk's JWT names the venue, so a player cannot ask for
     * another one's. Every active Survivor promotion comes back in one call
     * and stacks in that order (SUR-465).
     */
    /* SUR-467 — the same boards, addressed by the venue's public slug.
     *
     * No Authorization header: a URL somebody types has no session behind it,
     * which is the whole point of this route. The server takes the venue from
     * the slug rather than from a token.
     */
    getSurvivorLeaderboardBySlug(slug, limit){
        var config = {
            method: 'get',
            url: API_URL + `/survivor/leaderboard/${encodeURIComponent(slug)}`
                 + (limit ? `?limit=${limit}` : ""),
            headers: { 'Content-Type': 'application/json' }
        };

        return axios(config).then(response => {
            return response.data;
        });
    }

    getSurvivorLeaderboard(limit, playerId){
        // SUR-467/468 -- no limit means the whole field, which is what a
        // player needs to find themselves. playerId is what gets their own
        // tickets pinned to the top.
        var query = [];
        if(limit) query.push(`limit=${limit}`);
        if(playerId) query.push(`player_id=${encodeURIComponent(playerId)}`);
        var config = {
            method: 'get',
            url: API_URL + `/survivor/leaderboard${query.length ? "?" + query.join("&") : ""}`,
            headers: authHeaders()
        };

        return axios(config).then(response => {
            return response.data;
        });
    }

    /* SUR-501..SUR-510 — one ticket, one week, everything it can pick.
     *
     * Its own endpoint, not /players/play/. That one answers the entry
     * manager's question (every ticket across every week) and carries none of
     * what a pick needs: which teams this ticket has spent, which games have
     * kicked off, and the event choice ids to submit. The pick screen reading
     * it is what rendered the screen blank — every field it asked for was
     * simply absent, so nothing threw and an empty div was drawn.
     *
     * ticketid may be null: the entry manager offers PLAY on a row that holds
     * no ticket yet (SUR-437), and the submission creates it.
     */
    getSurvivorPickScreen(playerid, promoid, periodid, ticketid){
        var params = new URLSearchParams({
            playerid: playerid,
            promoid: promoid
        });
        if(periodid) params.set("periodid", periodid);
        if(ticketid) params.set("ticketid", ticketid);

        var config = {
            method: 'get',
            url: API_URL + `/survivor/pick?${params.toString()}`,
            headers: authHeaders()
        };

        return axios(config).then(response => {
            return response.data;
        });
    }

    /* Survivor Pool. One pick, attached to the ticket that spans the season.
     *
     * `ticketid` names an existing ticket; omitting it starts a new one, which
     * the server creates and records this pick against in one transaction.
     *
     * The key is `ticketid` because that is what the server reads. It used to
     * be sent as `promoplayerid` — the name of the model SUR-101 retired — so
     * the id never arrived, every pick looked like a request to *start* a
     * ticket, and SUR-301 refused them all from week 2 onward with "New tickets
     * could only be started in Week 1." A player could make their first pick
     * and never a second.
     *
     * Unlike the other game types this endpoint answers failures with a real
     * status and {code, message}: elimination is permanent, so a player who is
     * refused has to be told which rule stopped them. Callers should surface
     * error.response.data.message rather than a generic retry.
     */
    postSurvivorTicket(playerid, promoid, promoperiodid, eventchoiceidslist, playedatlocal, ticketid = null){
        var payload = {
            "playerid": playerid,
            "promoid": promoid,
            "promoperiodid": promoperiodid,
            "eventchoiceidslist": eventchoiceidslist,
            "playedatlocal": playedatlocal
        };
        if(ticketid) payload["ticketid"] = ticketid;

        var config = {
            method: 'post',
            url: API_URL + "/tickets",
            headers: authHeaders(),
            data : JSON.stringify(payload)
        };

        return axios(config).then(response => {
            return response.data;
        });
    }

    // The Survivor pre-pick screen, in one call. SUR-401..SUR-404.
    //
    // Returns every active promotion for the kiosk's venue: Survivor ones carry
    // the full entry-manager grid, the rest carry name and logo for a Play Now
    // button. One request rather than one per promotion, because the screen
    // stacks them and a screen that fans out N calls renders in pieces.
    //
    // The venue is taken from the JWT, never from here. Everything survival- or
    // deadline-related is resolved server-side; the client renders, it does not
    // decide. Notably there is no local timestamp: the daily limit is computed
    // in the venue's timezone (SUR-305a), so a device clock is not an input.
    getSurvivorPrePick(playerid){
        var config = {
            method: 'get',
            url: API_URL + `/survivor/prepick?playerid=${playerid}`,
            headers: authHeaders()
        };

        return axios(config).then(response => {
            return response.data;
        });
    }

    playGame(cardnumber, promoid, time, playerid = null){
        var config = {
            method: 'post',
            url: API_URL + "/players/play/",
            headers: authHeaders(),
            data : JSON.stringify({
                "cardnumber": cardnumber || "",
                "playerid": playerid,
                "promoid": promoid,
                "currentlocaltime": time,
            })
        };

        return axios(config).then(response => {
            //console.log(response);
            return response.data;
        });
    }

    /**
     * GEO-301 -- hand one location reading to the server and get the gate
     * descriptor back.
     *
     * `simulate` is development only. The server ignores it unless
     * SURVIVOR_CLOCK_OVERRIDE is on, and apps.py refuses to boot with that set
     * outside DEBUG -- so it cannot reach a machine serving real players. It
     * exists because a desktop has no GPS and, on Windows, the operating
     * system's location switch sits above the browser's: without it the
     * on-site path cannot be exercised at all except by standing inside a real
     * fence.
     */
    /**
     * GEO-610 -- tell the server what the browser said, and nothing else.
     *
     * A third of web visits over the promotion's first week produced no
     * location row at all, because every non-granted answer was handled
     * entirely inside OnsiteGate and never left the handset. Those visits are
     * currently indistinguishable from somebody who opened the link and
     * wandered off. This makes them distinguishable.
     *
     * Three properties, all of which matter more than the data:
     *
     * 1. **It never rejects.** Always resolves. A caller cannot forget a
     *    .catch, because there is nothing to catch -- same contract as
     *    readPosition, for the same reason.
     * 2. **It never touches the visit.** Deliberately NOT axios: the axios
     *    path shares interceptors and error shapes with the real calls, and a
     *    401 from a stale token there routes into the "this visit is over"
     *    handling (WEB-411) that navigates the player's page to /go/<slug>.
     *    Telemetry that can move somebody's page is worse than no telemetry.
     *    fetch, unshared, response ignored.
     * 3. **keepalive**, so a report sent while the page is going away still
     *    goes. sendBeacon would be the usual tool but cannot carry an
     *    Authorization header, which would mean an unauthenticated endpoint --
     *    a new public surface to save one fetch option.
     */
    reportOnsite(sessionId, promoid, outcome){
        if(!sessionId || !outcome) return Promise.resolve(false);
        try {
            return fetch(API_URL + "/launch/onsite/report", {
                method: "POST",
                headers: authHeaders(),
                keepalive: true,
                body: JSON.stringify({
                    "sid": sessionId,
                    "promoid": promoid,
                    "outcome": outcome,
                }),
            }).then(() => true).catch(() => false);
        } catch (e) {
            // No fetch at all, or it threw synchronously on the options.
            return Promise.resolve(false);
        }
    }

    verifyOnsite(sessionId, promoid, position, simulate){
        var config = {
            method: 'post',
            url: API_URL + "/launch/onsite",
            headers: authHeaders(),
            data : JSON.stringify({
                "sid": sessionId,
                "promoid": promoid,
                "latitude": position && position.latitude,
                "longitude": position && position.longitude,
                "accuracy": position && position.accuracy,
                "simulate": !!simulate,
            })
        };

        return axios(config).then(response => {
            return response.data;
        });
    }
}

export default new GameService();
